/**
 * Class related to javascript and HTML events
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
export class EventMethods {

    /**
     * Compute the pointer position relative to a target element that triggers this function call
     * @param {Event} event - event that triggers this function call containing the event target
     * @param {Number} offsetXLeft - offset in pixel to set a new starting point where the computation of the pointer position should start on the left side of the event target
     * @param {Number} offsetXRight - endpoint to end on the right side respectively
     * @param {Number} offsetYTop - starting point to start on the top respectively
     * @param {Number} offsetYBottom - endpoint to end on the bottom respectively
     * @returns {Array} Return the x and y coordinate of the pointer position relative to the event target
     */
    static getPointerPositionRelativeToEventTarget(event, offsetXLeft = 0, offsetXRight = 0, offsetYTop = 0, offsetYBottom = 0) {
        const eventTargetElement = event.target.getBoundingClientRect();
        let relativePositionX = (event.clientX - (eventTargetElement.x + offsetXLeft)) / (eventTargetElement.width - (offsetXLeft + offsetXRight));
        let relativePositionY = (event.clientY - (eventTargetElement.y + offsetYTop)) / (eventTargetElement.height - (offsetYTop + offsetYBottom));
        relativePositionX = Math.min(Math.max(0, relativePositionX), 1);
        relativePositionY = Math.min(Math.max(0, relativePositionY), 1);
        return [relativePositionX, relativePositionY]
    }

    /**
     * Compute the pointer position relative to a seek bar, i.e., HTML 'input' element where the 'type' attribute is 'range'
     * @param {Event} event - event that triggers this function call containing the event target
     * @returns {Array} Return the x and y coordinate of the pointer position relative to the seek bar
     */
    static getPointerPositionRelativeToSeekbar(event) {
        const isFinePointer = window.matchMedia("(pointer: fine)").matches;
        let [offsetXLeft, offsetXRight, offsetYTop, offsetYBottom] = [0, 0, 0, 0];
        if (isFinePointer) {
            const eventTargetElement = event.target.getBoundingClientRect();
            offsetXLeft = eventTargetElement.height / 2;
            offsetXRight = offsetXLeft;
            offsetYTop = eventTargetElement.width / 2;
            offsetYBottom = offsetYTop;
        }
        const [relativePositionX, relativePositionY] = EventMethods.getPointerPositionRelativeToEventTarget(event, offsetXLeft, offsetXRight, offsetYTop, offsetYBottom);
        return [relativePositionX, relativePositionY]
    }

    /**
     * Set the position of a seek bar pointer event in relation to its numeric value (that represents the time)
     * @param {Event} event - event that triggers this function call containing the seek bar that is an HTML 'input' with the 'max' attribute
     * @param {boolean} invert - flip start and end position
     * @returns {Array} Return the time for the x and y position of the pointer 
     */
    static getTimeValueOnSeekbarByDirectSeekbarEvent(event, invert = false) {
        let [relativePositionX, relativePositionY] = EventMethods.getPointerPositionRelativeToSeekbar(event);
        let timePositionX; let timePositionY;
        if (invert) {
            timePositionX = (1 - relativePositionX) * event.target.max;
            timePositionY = (1 - relativePositionY) * event.target.max;
        }
        else {
            timePositionX = relativePositionX * event.target.max;
            timePositionY = relativePositionY * event.target.max;
        }
        return [timePositionX, timePositionY]
    }
}

/**
 * Class that provide the functionality of javascript intervals via timeouts.
 * Advantage: Between intervals, the interval time can be changed. This is not possible in javascript with the built-in setInterval() 
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
export class IntervalTimeout {

    /**
     * Build an interval timeout object
     * @param {callback} callback - function that is called at the intervals 
     * @param {string} delayMS - execution intervals in milliseconds
     */
    constructor(callback, delayMS) {
        this.delayMS = delayMS;
        this.timeoutID;
        this.callback = callback;
    }

    /**
     * Clear the timer set with setInterval()
     */
    clearInterval() {
        clearInterval(this.timeoutID);
    }

    /**
     * Execute the callback via a timer repetitively.
     */
    setInterval() {
        if (isFinite(this.delayMS)) {
            this.callback();
            this.timeoutID = setTimeout(this.setInterval.bind(this), this.delayMS);
        }
        else
            this.timeoutID = setTimeout(this.setInterval.bind(this), 100);
    }
}

/**
 * Class that provides tools for time conversions
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
export class TimeConversion {

    /**
     * Convert the ISO time format (i.e., 12:34:56.789) to milliseconds (i.e., 45296789)
     * @param {string} hhmmssf - ISO time format with hours (HH), minutes (MM), seconds (SS) and milliseconds (f). Schema: 'HH:MM:SS.f'
     * @returns {string} Time in milliseconds
     */
    static isoDayTime2Milliseconds(hhmmssf) {
        let [hhmmss, f] = hhmmssf.split('.');
        let [hh, mm, ss] = hhmmss.split(':');
        return parseInt(f) + 1000 * (parseInt(ss) + 60 * (parseInt(mm) + parseInt(hh) * 60))
    }

    /**
     * Convert milliseconds (i.e., 45296789) to the ISO time format (i.e., 12:34:56.789)
     * @param {string} milliseconds - Time in milliseconds
     * @returns {string} ISO time format with hours (HH), minutes (MM), seconds (SS) and milliseconds (f). Schema: 'HH:MM:SS.f'
     */
    static milliseconds2IsoDayTime(milliseconds) {
        return new Date(milliseconds).toISOString().slice(11, 23)
    }

    /**
     * Convert a time string (from hours to milliseconds) to ISO time format
     * @param {string} time - time in a flexible format, e.g., 2:4:5, 02:04:05, 2:4:5.6, 02:04:05.600, 4:5.600, 5.6, 5, 123.4
     * @returns {string} ISO time format with hours (HH), minutes (MM), seconds (SS) and milliseconds (f). Schema: 'HH:MM:SS.f'
     */
    static time2IsoDayTime(time) {
        let [time_without_ms, f] = time.split('.');
        const time_array = time_without_ms.match(/(?:\d+)/g)

        let time_array_reverse = time_array.reverse();
        while (3 > time_array_reverse.length)
            time_array_reverse.push(0);
        let [ss, mm, hh] = time_array_reverse;

        let tmp = (f) ? f + "00" : "0";
        let ms = tmp.slice(0, 3);

        const milliseconds = parseInt(ms) + 1000 * (parseInt(ss) + 60 * (parseInt(mm) + parseInt(hh) * 60));
        return TimeConversion.milliseconds2IsoDayTime(milliseconds)
    }
}

/**
 * Class that provides tools to determine device properties
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
export class DeviceProperties {

    /**
     * Check device for touch screen
     * @returns {boolean} 
     */
    static isTouchDevice() {
        return (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0));
    }
}

/**
 * Class that provides tools to determine element properties
 * @memberof vef.ui.media
 * @author ckraemme <ckraemme@awi.de>
 * @private
 */
export class ElementProperties {

    /**
     * Check if an element is in the viewport
     * @param {object} element - JS element
     * @param {number} deviation - Allowed deviation, e.g., 0.4 means, 40 % of the width and 40 % of the height are allowed outside the viewport
     * @returns {boolean} 
     */
    static isInViewport(element, deviation = 0.0) {
        const rect = element.getBoundingClientRect();
        window.rect = rect;
        const height = rect.height
        return (
            rect.top >= -1 * rect.height * deviation &&
            rect.left >= -1 * rect.width * deviation &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) + rect.height * deviation &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth) + rect.width * deviation
        );
    }

}